import {
  PathsV1VehicleUuidGetParametersQueryExtend,
  PathsV1VehicleValuationUuidGetParametersQueryExtend,
  VehicleValuationStatus,
} from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useVehicleBuyInCreateDataQuery } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-create-data.query";
import {
  type VehicleBuyInCreateBody,
  useVehicleBuyInCreateMutation,
} from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-create.mutation";
import { useVehicleValuationByUuidQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-by-uuid.query";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { VehicleBuyInForm } from "../_components/vehicle-buy-in-form";
import { resolveVehicleBuyInCreateErrorDescription } from "./_helpers/vehicle-buy-in-create-error-description";

export function VehicleBuyInCreateView() {
  const { t } = useTranslation(["fab", "common"]);
  const [, navigate] = useLocation();
  const search = useSearch();
  const vehicleValuationUuid = new URLSearchParams(search).get("vehicleValuationUuid") ?? "";
  const valuationQuery = useVehicleValuationByUuidQuery(vehicleValuationUuid, {
    parameters: {
      extend: [
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
        PathsV1VehicleValuationUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
      ],
    },
  });
  const vehicleUuid = valuationQuery.data?.vehicle?.uuid ?? "";
  const vehicleQuery = useVehicleByUuidQuery(vehicleUuid, {
    parameters: {
      extend: [
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleUuidGetParametersQueryExtend.ExchangeEngine,
      ],
    },
  });
  const createDataQuery = useVehicleBuyInCreateDataQuery(vehicleValuationUuid);
  const createMutation = useVehicleBuyInCreateMutation();

  useEffect(() => {
    if (!vehicleValuationUuid) {
      navigate("~/fab/buy-in", { replace: true });
    }
  }, [navigate, vehicleValuationUuid]);

  async function handleSubmit(body: VehicleBuyInCreateBody): Promise<void> {
    await toastMutationStatus({
      mutationFn: () => createMutation.mutateAsync(body),
      successMessage: t("fab:VehicleBuyInCreated"),
      errorMessage: t("fab:VehicleBuyInCreateError"),
      resolveErrorMessage: resolveVehicleBuyInCreateErrorDescription,
      onSuccess: () => navigate("~/fab/buy-in"),
    });
  }

  if (!vehicleValuationUuid) return null;

  if (valuationQuery.isPending || vehicleQuery.isPending || createDataQuery.isPending) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Form}
        title={t("fab:CreateVehicleBuyIn")}
        subTitle={t("common:CompleteRequiredInformationDescription")}
      >
        <div className="flex min-h-64 items-center justify-center">
          <Loader size={LoaderSize.Medium} />
        </div>
      </CenteredLayout>
    );
  }

  if (valuationQuery.isError || vehicleQuery.isError || createDataQuery.isError) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Form}
        title={t("fab:CreateVehicleBuyIn")}
        subTitle={t("common:CompleteRequiredInformationDescription")}
      >
        <Alert variant="error">
          <AlertTitle>{t("fab:VehicleBuyInLoadError")}</AlertTitle>
          <AlertDescription>{t("fab:VehicleBuyInLoadErrorDescription")}</AlertDescription>
        </Alert>
      </CenteredLayout>
    );
  }

  const valuation = valuationQuery.data;
  const vehicle = vehicleQuery.data;
  const createData = createDataQuery.data;
  if (!valuation || !vehicle || !createData) return null;

  if (valuation.status !== VehicleValuationStatus.Confirmed) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Form}
        title={t("fab:CreateVehicleBuyIn")}
        subTitle={t("common:CompleteRequiredInformationDescription")}
      >
        <Alert variant="warning">
          <AlertTitle>{t("fab:VehicleValuationNotConfirmed")}</AlertTitle>
          <AlertDescription>{t("fab:VehicleValuationMustBeConfirmedForBuyIn")}</AlertDescription>
        </Alert>
      </CenteredLayout>
    );
  }

  if (createData.branches.length === 0) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Form}
        title={t("fab:CreateVehicleBuyIn")}
        subTitle={t("common:CompleteRequiredInformationDescription")}
      >
        <Alert variant="error">
          <AlertTitle>{t("fab:NoAssignedBranch")}</AlertTitle>
          <AlertDescription>{t("fab:NoAssignedBranchDescription")}</AlertDescription>
        </Alert>
      </CenteredLayout>
    );
  }

  return (
    <Can I="create" a="VehicleBuyIn">
      <CenteredLayout
        variant={CenteredLayoutVariant.Form}
        title={t("fab:CreateVehicleBuyIn")}
        subTitle={t("common:CompleteRequiredInformationDescription")}
      >
        <VehicleBuyInForm
          valuation={valuation}
          vehicle={vehicle}
          createData={createData}
          vehicleValuationUuid={vehicleValuationUuid}
          isPending={createMutation.isPending}
          onCancel={() => navigate(`~/fab/list/${vehicleValuationUuid}`)}
          onSubmit={handleSubmit}
        />
      </CenteredLayout>
    </Can>
  );
}
